import {
  PracticasDocument,
  PracticasFieldsFragment,
  PracticasQuery,
  PracticasQueryVariables,
  LanguageId,
  TaxonomyTermCbzEducation,
  TaxonomyTermCbzRegion,
} from '../graphql/generated'
import type { ApolloQueryResult, OperationVariables, QueryOptions } from '@apollo/client'
import { useTypedApolloClients } from '~/lib/utils/graphqlDocument'

type ApolloQueryFn = <TData, TVariables extends OperationVariables>(
  options: QueryOptions<TVariables, TData>,
) => Promise<ApolloQueryResult<TData>>

export interface PracticasItem {
  id: string
  title: string
  cbzEducation: string
  cbzEducationTid: string
  cbzRegion: string
  cbzRegionTid: string
  studyMode: string[]
  city: string
  company: string
  internship: {
    isAvailable: boolean
    mail: string
    phone: string
    website: string
    text: string
  }
  employment: {
    isAvailable: boolean
    mail: string
    phone: string
    website: string
    text: string
  }
}

const transformResponse = (data: PracticasFieldsFragment[]): PracticasItem[] => {
  return (
    data?.map((item) => {
      const studyMode = (item.fieldStudyMode?.filter(Boolean) as string[]) ?? []
      return {
        id: item.entityId || '',
        title: item.title || '',
        cbzEducation: (item.fieldCbzEducation?.entity as TaxonomyTermCbzEducation)?.name || '',
        cbzEducationTid: (item.fieldCbzEducation?.entity as TaxonomyTermCbzEducation)?.tid?.toString() || '',
        cbzRegion: (item.fieldCbzRegion?.entity as TaxonomyTermCbzRegion)?.name || '',
        cbzRegionTid: (item.fieldCbzRegion?.entity as TaxonomyTermCbzRegion)?.tid?.toString() || '',
        studyMode: studyMode.length ? studyMode : ['full_time'],
        city: item.fieldCity || '',
        company: item.fieldCompany || '',
        internship: {
          isAvailable: item.fieldInternshipAvailable || false,
          mail: item.fieldInternshipMail || '',
          phone: item.fieldInternshipPhone || '',
          website: item.fieldInternshipWebsite?.url?.path || '',
          text: item.fieldInternshipText?.processed || '',
        },
        employment: {
          isAvailable: item.fieldEmploymentAvailable || false,
          mail: item.fieldEmploymentMail || '',
          phone: item.fieldEmploymentPhone || '',
          website: item.fieldEmploymentWebsite?.url?.path || '',
          text: item.fieldEmploymentText?.processed || '',
        },
      }
    }) || []
  )
}

const getItems = async (
  query: ApolloQueryFn,
  variables: PracticasQueryVariables,
): Promise<{
  items: PracticasItem[]
  total: number
}> => {
  const { data } = await query<PracticasQuery, PracticasQueryVariables>({
    query: PracticasDocument,
    variables,
  })
  return {
    items: transformResponse(data.nodeQuery?.entities as PracticasFieldsFragment[]),
    total: data.nodeQuery?.count || 0,
  }
}

export const usePracticas = async ({
  filter,
}: {
  filter: {
    education: string[]
    region: string[]
    studyMode: string[]
  }
}) => {
  const config = useRuntimeConfig()
  const { languageCODE } = useLocale()
  const clients = useTypedApolloClients()

  const page = ref(1)
  const perPage = 18

  const allStudyModes = ['full_time', 'part_time']

  const { state: data, ready } = useAsyncState({
    key: 'practicas',
    fetcher: () => getItems(clients!.default.query, { language: languageCODE.value as LanguageId }),
    fallback: { items: [], total: 0 },
  })

  const allItems = computed(() => data.value.items)

  const partTimeEducationIds = computed(() => [
    ...new Set(
      allItems.value.filter((item) => item.studyMode.includes('part_time')).map((item) => item.cbzEducationTid),
    ),
  ])

  const showStudyMode = computed(() => filter.education.some((id) => partTimeEducationIds.value.includes(id)))

  const effectiveStudyMode = computed(() =>
    showStudyMode.value && filter.studyMode.length ? filter.studyMode : allStudyModes,
  )

  const filteredItems = computed(() =>
    allItems.value.filter(
      (item) =>
        filter.education.includes(item.cbzEducationTid) &&
        filter.region.includes(item.cbzRegionTid) &&
        item.studyMode.some((mode) => effectiveStudyMode.value.includes(mode)),
    ),
  )

  const maxPages = computed(() => Math.ceil(filteredItems.value.length / perPage))
  const items = computed(() => filteredItems.value.slice(perPage * (page.value - 1), perPage * page.value))

  watch(filter, () => (page.value = 1), { deep: true })

  const changePage = (newPage: number) => {
    page.value = newPage
  }

  const downloadUrl = computed(() => {
    return `${config.public.drupalApiUrl}/practicas-download?cbzEducationIds=${filter.education.join(
      ',',
    )}&cbzRegionId=${filter.region.length === 1 ? filter.region : null}&studyModes=${effectiveStudyMode.value.join(',')}`
  })

  await ready()

  return {
    items,
    page,
    maxPages,
    changePage,
    showStudyMode,
    downloadUrl,
  } as const
}
